import { request } from "./http";

export interface PlanOut {
  code: string;
  name: string;
  seats_label: string;
  max_users: number | null;
  tier: string;
  monthly_price_minor: number;
  yearly_price_minor: number;
  purchasable: boolean;
  description: string;
}

export interface PublicPlans {
  currency: string;
  periods: string[];
  plans: PlanOut[];
}

export interface BillingOrderOut {
  id: string;
  created_at: string | null;
  plan_code: string;
  period: string;
  amount_minor: number;
  currency: string;
  status: string;
  paid_at: string | null;
  valid_until: string | null;
  /** The short code to quote in the bank-transfer note. */
  reference: string | null;
  /** The UTR the learner reported after transferring. */
  payer_reference: string | null;
}

/** Where to send the money. Only the methods this deployment actually
 *  has — a payment screen listing one that does not work is worse than
 *  one listing fewer. */
export interface PayTo {
  configured: boolean;
  upi_id?: string;
  paypal_url?: string;
  bank?: { name: string; account: string; ifsc: string; holder: string };
}

export interface ManualOrder extends BillingOrderOut {
  pay_to: PayTo;
}

/** A row in the super-admin's approval queue. */
export interface PendingOrder extends BillingOrderOut {
  tenant_id: string;
  tenant_name: string | null;
}

export interface BillingState {
  configured: boolean;
  currency: string;
  plan_code: string | null;
  plan_name: string | null;
  tier: string;
  expires_at: string | null;
  days_left: number | null;
  seats_limit: number | null;
  seats_active: number;
  /** Access survives the paid horizon by the grace window — "expired" is
   *  not the same as "shut". */
  access_until?: string | null;
  in_grace?: boolean;
  grace_days?: number;
}

export interface MyBilling extends BillingState {
  orders: BillingOrderOut[];
}

export interface CheckoutSession {
  order_id: string;
  razorpay_order_id: string;
  key_id: string;
  amount_minor: number;
  currency: string;
  plan_code: string;
  period: string;
  name: string;
  description: string;
}

export interface RevenueSummary {
  currency: string;
  total_paid_minor: number;
  orders_paid: number;
  orders: {
    id: string;
    tenant: string;
    plan_code: string;
    period: string;
    amount_minor: number;
    currency: string;
    paid_at: string | null;
  }[];
}

export function publicPlans(): Promise<PublicPlans> {
  return request("/billing/plans", { anonymous: true });
}

export function myBilling(): Promise<MyBilling> {
  return request("/billing/");
}

export function createCheckout(planCode: string, period: string): Promise<CheckoutSession> {
  return request("/billing/checkout", {
    method: "POST",
    body: { plan_code: planCode, period },
  });
}

export function verifyPayment(
  orderId: string,
  paymentId: string,
  signature: string,
): Promise<MyBilling & { order: BillingOrderOut }> {
  return request("/billing/verify", {
    method: "POST",
    body: {
      order_id: orderId,
      razorpay_payment_id: paymentId,
      razorpay_signature: signature,
    },
  });
}

export function revenueSummary(): Promise<RevenueSummary> {
  return request("/billing/revenue");
}

// --- paying by bank transfer -----------------------------------------------
//
// There is no payment gateway yet: a learner transfers the money quoting a
// reference, reports their UTR, and a super-admin confirms it against the
// bank statement.

export function startManualOrder(
  planCode: string, period: string,
): Promise<ManualOrder> {
  return request("/billing/manual/order", {
    method: "POST",
    body: { plan_code: planCode, period },
  });
}

export function reportPayment(
  orderId: string, payerReference: string,
): Promise<BillingOrderOut> {
  return request(`/billing/manual/order/${orderId}/reported`, {
    method: "POST",
    body: { payer_reference: payerReference },
  });
}

export function pendingOrders(): Promise<PendingOrder[]> {
  return request("/billing/manual/pending");
}

export function approveOrder(
  orderId: string, note?: string,
): Promise<BillingOrderOut> {
  return request(`/billing/manual/pending/${orderId}/approve`, {
    method: "POST",
    body: { note: note ?? null },
  });
}

export function rejectOrder(
  orderId: string, reason: string,
): Promise<BillingOrderOut> {
  return request(`/billing/manual/pending/${orderId}/reject`, {
    method: "POST",
    body: { reason },
  });
}
